import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  API,
  AtomTypeDTO,
  AtomTypeGroupDTO,
  CullAtomDTO,
  CullDTO,
  CullWithAtomsDTO,
  PlayerCullDTO,
  TimeCullDTO,
} from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { SelectDetailsResult, SelectDetailsValue } from 'src/app/services/gui/modal/modal.model';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { ErrorService } from '../../../services/error.service';
import { AtomRow } from './atom-row';
import { UtilService } from '../../../services/util.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-filter-definition',
  templateUrl: './sb-filter-definition.component.html',
  styleUrls: ['./sb-filter-definition.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class SbFilterDefinitionComponent implements OnInit, ModalNavigationComponent {
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() isInModalContext: boolean;
  @Input() preselectType: boolean;

  DEBUGGING: boolean = false;

  DOINDENTATION: boolean = true;

  tempCullAtom: CullAtomDTO;

  rows: AtomRow[] = [];

  fetchsetup: any;

  _atomGroups: AtomTypeGroupDTO[];

  _atomTypes: Map<string, AtomTypeDTO>;

  _cull: CullWithAtomsDTO;

  loading: boolean = true;

  get cull(): CullWithAtomsDTO {
    return this._cull;
  }

  @Input() set cull(val: CullWithAtomsDTO) {
    this._cull = val;
    // this.parse(); //don't do that here you twat
  }

  @Output() cullChange: EventEmitter<CullDTO> = new EventEmitter<CullDTO>();

  _fetchcull: CullWithAtomsDTO;

  @Input() disabled: boolean = false;
  inDeleteProcess: boolean = false;

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    private modal: ModalService,
    public translate: TranslateService,
    public util: UtilService,
    private destroyRef: DestroyRef
  ) {
    this.isInModalContext = false;
  }

  ngOnInit() {
    if (!this._cull?.type) {
      return;
    }
    if (this.cull.id) {
      const fetchcull: CullWithAtomsDTO = this.cull;
      this.api
        .cs_Cull_atomSetupForCullAtoms(fetchcull.atoms)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (fetchsetup: any) => {
            this.api
              .cs_Cull_retrieveAtomTypeGroups({ type: this._cull.type })
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                take(1),
                finalize(() => {
                  this.loading = false;
                })
              )
              .subscribe({
                next: (atomGroups: AtomTypeGroupDTO[]) => {
                  this.fetchsetup = fetchsetup;
                  this.atomGroups = atomGroups;
                  this.fetchcull = fetchcull;
                  for (const i in fetchcull.atoms) {
                    if (i) {
                      fetchcull.atoms[i]['_setup'] = fetchsetup[i];
                    }
                  }
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.fetchcull = this.cull;
      this.api
        .cs_Cull_retrieveAtomTypeGroups({ type: this._cull.type })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (e: AtomTypeGroupDTO[]) => {
            this.atomGroups = e;
            for (const row of this.rows) {
              /*
               const atom: CullAtomDTO = this.api.createDTO('CullAtomDTO');
               atom.type = row.atom.type;
               */
              const atom = Object.assign({}, row.atom);
              delete atom.id;
              this.api
                .cs_Cull_atomSetupForCullAtom(atom)
                .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                .subscribe(receivedSetup => {
                  row.setup = receivedSetup;
                });

              /*
              this.api.cs_Cull_retrieveAtomSetupDTOByAtomType(row.atom.type).subscribe((setup: CullAtomSetupDTO)=> {
              row.setup = setup;
              });
              */
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  get atomTypes(): Map<string, AtomTypeDTO> {
    return this._atomTypes;
  }

  get fetchcull(): CullWithAtomsDTO {
    return this._fetchcull;
  }

  set fetchcull(val: CullWithAtomsDTO) {
    this._fetchcull = val;
    this.parse();
  }

  get atomGroups(): AtomTypeGroupDTO[] {
    return this._atomGroups;
  }

  set atomGroups(atomGroups: AtomTypeGroupDTO[]) {
    this._atomGroups = atomGroups;
    const atomTypes: Map<string, AtomTypeDTO> = new Map();
    if (atomGroups) {
      for (const atomGroup of atomGroups) {
        for (const atomType of atomGroup.atomTypes) {
          atomTypes.set(atomType.id, atomType);
        }
      }
    }
    this._atomTypes = atomTypes;
  }

  resolveAtom(id: string): CullAtomDTO {
    if (this._fetchcull) {
      for (const atom of this._fetchcull.atoms) {
        if (atom.id.num === +id) {
          return atom;
        }
      }
    }
  }

  parse() {
    if (this._fetchcull) {
      let replacePars = false;

      let form = this._fetchcull.expression;

      if (form) {
        if (!form.includes('(') && !form.includes(')')) {
          replacePars = true;
        }

        if (replacePars) {
          form = '(' + form;
          form = form + ')';
        }

        const tokens: string[] = this.tokenize(form);

        this.rows = this.parseTokens(tokens, replacePars, this.DOINDENTATION);
      }
    }
  }

  parseTokens(tokens: string[], replacePars: boolean, doIndentation: boolean) {
    const rows: AtomRow[] = [];
    let row: AtomRow = { left: [], atom: null, right: [] };

    for (let i = 0; i < tokens.length; i++) {
      let prev: string = null;
      const curr: string = tokens[i];
      let next: string = null;
      if (i - 1 >= 0) {
        prev = tokens[i - 1];
      }
      if (i + 1 < tokens.length) {
        next = tokens[i + 1];
      }

      if (i === 0 && this.isOperator(curr)) {
        row.left.push(curr);
      } else {
        if (curr === '(') {
          row.left.push(curr);
        }
        if (curr === '!') {
          row.left.push(curr);
        }

        if (this.isNumber(curr)) {
          row.atom = this.resolveAtom(curr);
          if (this.isOperator(next)) {
            rows.push(row);
            const arr = [next];
            for (let y = i + 2; y < tokens.length; y++) {
              if (this.isOperator(tokens[y])) {
                i = y;
                arr.push(tokens[y]);
              } else {
                break;
              }
            }
            row = { left: arr, atom: null, right: [] };
          } else if (!next) {
            rows.push(row);
            break;
          }
        }

        if (curr === ')') {
          for (let y = i + 1; y < tokens.length; y++) {
            if (tokens[y] === ')') {
              i = y;
              row.right.push(tokens[y]);
              if (i + 1 < tokens.length) {
                next = tokens[i + 1];
              }
            } else {
              break;
            }
          }
          row.right.push(curr);
          rows.push(row);
          row = { left: [next], atom: null, right: [] };
        }
      }
    }

    if (replacePars) {
      for (const pRow of rows) {
        for (let i = 0; i < pRow.left.length; i++) {
          if (pRow.left[i] === '(') {
            pRow.left.splice(i, 1);
          }
        }
        for (let i = 0; i < pRow.right.length; i++) {
          if (pRow.right[i] === ')') {
            pRow.right.splice(i, 1);
          }
        }
      }
    }

    if (doIndentation) {
      let indentation = 0;

      for (const indentationRow of rows) {
        for (let ind = 0; ind < indentation; ind++) {
          indentationRow.left.unshift('');
        }
        if (indentationRow.left.includes('(')) {
          for (const l of indentationRow.left) {
            if (l === '(') {
              indentation++;
            }
          }
        }
        if (indentationRow.right.includes(')')) {
          for (const r of indentationRow.right) {
            if (r === ')') {
              indentation--;
            }
          }
        }

        if (indentationRow.left.length === 0) {
          indentationRow.left.push(' ');
        }
      }
    }
    if (rows && rows.length === 1 && !rows[0].atom) {
      return [];
    }

    for (const lRow of rows) {
      this.clearWhiteSpaces(lRow.left);
      this.clearWhiteSpaces(lRow.right);
    }
    return rows;
  }

  clearWhiteSpaces(arr: any[]) {
    if (arr?.length) {
      let lIndex = arr.indexOf(' ', 0);
      while (lIndex > -1) {
        arr.splice(lIndex, 1);
        lIndex = arr.indexOf(' ', 0);
      }
    }
  }

  tokenize(formula: string): string[] {
    const tokens: string[] = [];
    const form = formula.trim().replace(' ', '');

    let s = '';

    for (let i = 0; i < form.length; i++) {
      const curr: string = form[i];
      let next = null;
      s += curr;
      if (i + 1 < form.length) {
        next = form[i + 1];
      }
      if (this.isNumber(s.trim()) && !this.isNumber(next)) {
        tokens.push(s.trim());
        s = '';
        continue;
      }
      if (this.isSymbol(s.trim())) {
        tokens.push(s.trim());
        s = '';
        continue;
      }
      if (i === form.length - 1) {
        // check whether if-clause with continue is necessary:  continue;
      }
    }

    return tokens;
  }

  isOperator(e: string) {
    switch (e) {
      case '&':
      case '|':
      case '!':
        return true;
      default:
        return false;
    }
  }

  isSymbol(e: string) {
    switch (e) {
      case '(':
      case ')':
      case '&':
      case '|':
      case '!':
        return true;
      default:
        return false;
    }
  }

  isNumber(num: any) {
    if (!num) {
      return false;
    }
    return !isNaN(num);
  }

  showPreModal(bottom: boolean) {
    const selectDetailsValues: SelectDetailsValue<string>[] = [];
    for (const group of this.atomGroups) {
      for (const type of group.atomTypes) {
        selectDetailsValues.push({
          name: this.translate.instant('lang.cull.' + this.stringTransformation.toCamelCase(type.name)),
          value: type.id,
          description: this.translate.instant('lang.cull.' + this.stringTransformation.toCamelCase(type.name) + '.description'),
        });
      }
    }
    this.modal
      .selectWithDetails({
        values: selectDetailsValues,
        initialValue: null,
        yes: this.translate.instant('lang.globals.ok'),
        no: this.translate.instant('lang.globals.cancel'),
        message: this.translate.instant('lang.culls.addRowCaption'),
        title: this.translate.instant('lang.culls.addRowTitle'),
        modalOptions: { size: 'xl' },
      })
      .then((e: SelectDetailsResult<string>) => {
        if (e?.value) {
          this.loading = true;
          const atom: CullAtomDTO = this.api.createDTO('CullAtomDTO');
          atom.type = e.value.value;
          atom._type = e.value.value;

          this.tempCullAtom = atom;
          this.api
            .cs_Cull_atomSetupForCullAtom(atom)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              take(1),
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe({
              next: receivedSetup => {
                this.tempCullAtom.id = this.getNextId();
                this.tempCullAtom.id.cull = { id: this.cull ? this.cull.id : null };

                let arrleft = [];

                if (this.rows?.length !== 0) {
                  arrleft = ['&'];
                }

                const row = { left: [], atom: this.tempCullAtom, setup: receivedSetup, right: [] };

                if (bottom) {
                  row.left = arrleft;
                  this.rows.push(row);
                } else {
                  if (this.rows.length > 0) {
                    this.rows[0].left.unshift(...arrleft);
                  }
                  this.rows.unshift(row);
                }
                this.tempCullAtom = null;

                this.refreshCullAndEmit().then();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch((e: any) => {
        this.errorHandler.errorHandler(e);
      });
  }

  getNextId() {
    let id: number = 1;
    for (const row of this.rows) {
      const atom = row.atom;
      if (atom?.id?.num) {
        while (+atom.id.num >= id) {
          id++;
        }
      }
    }
    return { num: id };
  }

  addRow(bottom: boolean) {
    this.showPreModal(bottom);
  }

  refreshCullAndEmit() {
    return new Promise(resolve => {
      this.parseBackwards();
      this.cull = this.fetchcull;
      this.cullChange.emit(this.cull);
      resolve(true);
    });
  }

  save() {
    this.parseBackwards();

    let obs: Observable<CullWithAtomsDTO> = null;

    switch (this.fetchcull?.type) {
      case 'TimeCull':
        obs = this.api.cs_Cull_updateTimeCull(this.cull as TimeCullDTO);
        break;
      case 'PlayerCull':
        obs = this.api.cs_Cull_updatePlayerCull(this.cull as PlayerCullDTO);
        break;
      case 'GeoCull':
      default:
        break;
    }

    if (obs) {
      obs.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe({
        next: e => {
          this.fetchcull = e;
          this.cull = e;
          this.cullChange.emit(e);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    }
  }

  deleteRow(index: number) {
    this.inDeleteProcess = true;
    this.modal
      .confirm({
        title: this.translate.instant('lang.globals.deleteRow'),
        message: this.translate.instant('lang.globals.deleteRowDescription'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        // TODO refactor
        if (result) {
          if (this.rows.length > 0) {
            let bracketIndex = -1;
            for (let i = this.rows[index].left.length - 1; i >= 0; i--) {
              if (this.rows[index].left[i] === '(' && this.rows[index].right[0] !== ')') {
                bracketIndex = i;
                break;
              }
            }
            switch (true) {
              case index === 0:
                if (this.rows.length > 1) {
                  this.rows[index + 1].left.splice(0, 1);
                  if (bracketIndex > 0) {
                    this.rows[index + 1].left.unshift(...this.rows[index].left.slice(0, bracketIndex + 1));
                  } else if (bracketIndex === 0) {
                    this.rows[index + 1].left.unshift('(');
                  }
                }
                break;
              case index < this.rows.length - 1:
                if (bracketIndex === -1) {
                  this.rows[index - 1].right.push(...this.rows[index].right);
                } else {
                  this.rows[index + 1].left.splice(0, 1);
                  if (bracketIndex > 0) {
                    this.rows[index + 1].left.unshift(...this.rows[index].left.slice(0, bracketIndex + 1));
                  } else {
                    this.rows[index + 1].left.unshift('(');
                  }
                }
                break;
              case index === this.rows.length - 1:
                if (this.rows[index].right.length > 0) {
                  this.rows[index - 1].right.push(...this.rows[index].right);
                }
                break;
            }
          }
          this.rows.splice(index, 1);
          this.removeBrackets();
        }
        this.modal.dismissAll();
        this.refreshCullAndEmit().then(() => {
          this.inDeleteProcess = false;
        });
      });
  }

  parseBackwards() {
    let formula = '';
    const atoms = [];
    for (const row of this.rows) {
      if (row.left) {
        for (const l of row.left) {
          formula += l;
        }
      }
      formula += row.atom.id.num;
      atoms.push(row.atom);
      if (row.right) {
        for (const r of row.right) {
          formula += r;
        }
      }
    }
    this.fetchcull.atoms = atoms;
    this.fetchcull.expression = formula;
  }

  private removeBrackets() {
    if (this.rows.length <= 0) {
      return;
    }
    for (const row of this.rows) {
      if (row.right.length > 0) {
        for (let i = row.left.length - 1; i >= 0; i--) {
          if (row.left[i] === '(') {
            row.left.splice(i, 1);
            row.right.splice(0, 1);
            break;
          }
          if (row.left[i] === '!') {
            continue;
          }
          if (row.left[i] === '|' || row.left[i] === '&') {
            break;
          }
        }
      }
    }
  }
}
